// src/components/Recruiter/CandidateSearch.jsx
import React, { useState, useEffect } from 'react';
import { getAllUsers } from '../../services/admin';
import { getDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { deductTokens, getUnlockedCandidates, saveUnlockedCandidate } from '../../services/recruiter';
import CVPDFTemplate from './CVPDFTemplate';
import html2pdf from 'html2pdf.js';

// Cache keys
const CANDIDATES_CACHE_KEY = 'gradpath_candidates_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Calculate completion percentage (capped at 100%)
const calculateCompletion = (candidate) => {
    if (!candidate) return 0;
    
    const fields = [
        'firstName', 'lastName', 'phoneNumber', 'city', 'country', 
        'gender', 'dateOfBirth', 'lookingFor', 'preferredJobLocation', 
        'desiredSalary', 'professionalSummary'
    ];
    
    let completed = 0;
    let total = fields.length;
    
    fields.forEach(field => {
        if (candidate[field]?.trim()) completed++;
    });
    
    if (candidate.skills?.length >= 3) completed++;
    total++;
    
    if (candidate.workExperience?.length > 0 || candidate.education?.length > 0) completed++;
    total++;
    
    if (candidate.citizenship === 'South African') {
        total++;
        if (candidate.idNumber?.trim() && /^\d{13}$/.test(candidate.idNumber)) completed++;
    }
    
    return Math.min(Math.round((completed / total) * 100), 100);
};

// Check if profile is complete (>= 95%)
const isProfileComplete = (candidate) => {
    const completion = calculateCompletion(candidate);
    return completion >= 95;
};

// Helper: Get cached candidates
const getCachedCandidates = () => {
    try {
        const cached = localStorage.getItem(CANDIDATES_CACHE_KEY);
        if (cached) {
            const { data, timestamp } = JSON.parse(cached);
            // Check if cache is still valid
            if (Date.now() - timestamp < CACHE_DURATION) {
                return data;
            }
        }
        return null;
    } catch (error) {
        console.error('Error reading cache:', error);
        return null;
    }
};

// Helper: Save candidates to cache
const saveCandidatesToCache = (candidates) => {
    try {
        localStorage.setItem(CANDIDATES_CACHE_KEY, JSON.stringify({
            data: candidates,
            timestamp: Date.now()
        }));
    } catch (error) {
        console.error('Error saving to cache:', error);
    }
};

const CandidateSearch = ({ recruiterId, tokens, onTokenUpdate }) => {
    const [candidates, setCandidates] = useState([]);
    const [filteredCandidates, setFilteredCandidates] = useState([]);
    const [unlockedCandidates, setUnlockedCandidates] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [contactUnlocked, setContactUnlocked] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        // Try to load from cache first
        const cachedData = getCachedCandidates();
        if (cachedData && cachedData.length > 0) {
            setCandidates(cachedData);
            setFilteredCandidates(cachedData);
            setTotalPages(Math.ceil(cachedData.length / itemsPerPage));
            setLoading(false);
        }
        
        // Always fetch fresh data in background
        loadCandidates();
        loadUnlockedCandidates();
    }, [recruiterId]);

    const loadCandidates = async () => {
        // If we already have cached data, don't show loading spinner
        const hasCache = getCachedCandidates() !== null;
        if (!hasCache) {
            setLoading(true);
        } else {
            setIsRefreshing(true);
        }
        
        try {
            const users = await getAllUsers();
            const candidateUsers = users.filter(u => u.role === 'candidate' || !u.role);
            
            const candidateData = [];
            for (const user of candidateUsers) {
                const candidateDoc = await getDoc(doc(db, 'candidates', user.id));
                if (candidateDoc.exists()) {
                    const data = candidateDoc.data();
                    const completion = calculateCompletion(data);
                    
                    if (completion >= 95) {
                        if (!data.profileCompleted) {
                            try {
                                await updateDoc(doc(db, 'candidates', user.id), {
                                    profileCompleted: true
                                });
                            } catch (updateError) {
                                console.error("Error updating profileCompleted:", updateError);
                            }
                        }
                        
                        candidateData.push({
                            id: user.id,
                            ...user,
                            ...data,
                            _completion: completion
                        });
                    }
                }
            }
            
            // Update state
            setCandidates(candidateData);
            setFilteredCandidates(candidateData);
            setTotalPages(Math.ceil(candidateData.length / itemsPerPage));
            setCurrentPage(1);
            
            // Save to cache
            saveCandidatesToCache(candidateData);
            
        } catch (error) {
            console.error("Error loading candidates:", error);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    // Load unlocked candidates from Firebase
    const loadUnlockedCandidates = async () => {
        try {
            if (!recruiterId) return;
            
            const firebaseUnlocked = await getUnlockedCandidates(recruiterId);
            
            if (firebaseUnlocked && firebaseUnlocked.length > 0) {
                setUnlockedCandidates(firebaseUnlocked);
                localStorage.setItem(`unlocked_${recruiterId}`, JSON.stringify(firebaseUnlocked));
                return;
            }
            
            const localUnlocked = JSON.parse(localStorage.getItem(`unlocked_${recruiterId}`) || '[]');
            if (localUnlocked && localUnlocked.length > 0) {
                let migratedCount = 0;
                for (const candidateId of localUnlocked) {
                    const result = await saveUnlockedCandidate(recruiterId, candidateId);
                    if (result.success) migratedCount++;
                }
                if (migratedCount > 0) {
                    console.log(`Migrated ${migratedCount} unlocked candidates to Firebase`);
                }
                setUnlockedCandidates(localUnlocked);
            } else {
                setUnlockedCandidates([]);
            }
        } catch (error) {
            console.error("Error loading unlocked candidates:", error);
            try {
                const unlocked = JSON.parse(localStorage.getItem(`unlocked_${recruiterId}`) || '[]');
                setUnlockedCandidates(unlocked);
            } catch (localError) {
                console.error("Error loading from localStorage:", localError);
                setUnlockedCandidates([]);
            }
        }
    };

    // Manual refresh function
    const handleRefresh = () => {
        // Clear cache and reload
        localStorage.removeItem(CANDIDATES_CACHE_KEY);
        loadCandidates();
    };

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        
        if (!term) {
            setFilteredCandidates(candidates);
            setTotalPages(Math.ceil(candidates.length / itemsPerPage));
            setCurrentPage(1);
            return;
        }
        
        const filtered = candidates.filter(c => {
            const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase();
            const skills = c.skills?.join(' ').toLowerCase() || '';
            const city = (c.city || '').toLowerCase();
            const lookingFor = (c.lookingFor || '').toLowerCase();
            
            return fullName.includes(term) || 
                   skills.includes(term) || 
                   city.includes(term) ||
                   lookingFor.includes(term);
        });
        setFilteredCandidates(filtered);
        setTotalPages(Math.ceil(filtered.length / itemsPerPage));
        setCurrentPage(1);
    };

    const handleViewProfile = (candidate) => {
        setSelectedCandidate(candidate);
        const isUnlocked = unlockedCandidates.includes(candidate.id);
        setContactUnlocked(isUnlocked);
        setShowProfileModal(true);
    };

    const handleUnlockContact = async () => {
        if (tokens < 1) {
            alert('Insufficient tokens. Please contact admin to add more tokens.');
            return;
        }

        setUnlocking(true);
        try {
            const result = await deductTokens(
                recruiterId, 
                1, 
                `Unlocked contact info for: ${selectedCandidate.firstName} ${selectedCandidate.lastName}`
            );
            
            if (result.success) {
                const saveResult = await saveUnlockedCandidate(recruiterId, selectedCandidate.id);
                
                if (saveResult.success) {
                    const updatedUnlocked = [...unlockedCandidates, selectedCandidate.id];
                    setUnlockedCandidates(updatedUnlocked);
                    localStorage.setItem(`unlocked_${recruiterId}`, JSON.stringify(updatedUnlocked));
                    setContactUnlocked(true);
                    await onTokenUpdate();
                    alert('✅ Contact information unlocked successfully!');
                } else {
                    alert('Failed to save unlocked candidate. Please contact support.');
                    console.error('Failed to save unlocked candidate:', saveResult.error);
                }
            } else {
                alert('Failed to unlock contact info: ' + result.error);
            }
        } catch (error) {
            console.error("Error unlocking contact:", error);
            alert('Failed to unlock contact info. Please try again.');
        } finally {
            setUnlocking(false);
        }
    };

    const handleDownloadCV = async () => {
        if (!contactUnlocked) {
            alert('Please unlock contact information first to download the CV.');
            return;
        }

        const element = document.getElementById('cv-pdf-content');
        if (!element) return;

        try {
            const opt = {
                margin: [0.5, 0.5, 0.5, 0.5],
                filename: `CV_${selectedCandidate.firstName}_${selectedCandidate.lastName}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, letterRendering: true },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };
            await html2pdf().set(opt).from(element).save();
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    const isUnlocked = (candidateId) => {
        return unlockedCandidates.includes(candidateId);
    };

    const getDisplayCandidates = () => {
        let result = [];
        if (activeTab === 'unlocked') {
            result = filteredCandidates.filter(c => isUnlocked(c.id));
        } else {
            result = filteredCandidates;
        }
        
        // Paginate
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return result.slice(startIndex, endIndex);
    };

    const displayCandidates = getDisplayCandidates();

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setTotalPages(Math.ceil(filteredCandidates.length / newItemsPerPage));
        setCurrentPage(1);
    };

    // Pagination render helper
    const renderPagination = () => {
        const totalItems = activeTab === 'unlocked' 
            ? filteredCandidates.filter(c => isUnlocked(c.id)).length 
            : filteredCandidates.length;
        const totalPages = Math.ceil(totalItems / itemsPerPage);

        if (totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                        Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} candidates
                    </span>
                    <select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        className="ml-2 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:border-gradpath-green outline-none"
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-lg text-sm transition ${
                            currentPage === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'hover:bg-gray-100 text-gray-700'
                        }`}
                    >
                        <i className="fas fa-chevron-left"></i>
                    </button>
                    {pageNumbers.map(page => (
                        <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 rounded-lg text-sm transition ${
                                page === currentPage
                                    ? 'bg-gradpath-green text-white'
                                    : 'hover:bg-gray-100 text-gray-700'
                            }`}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-lg text-sm transition ${
                            currentPage === totalPages
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'hover:bg-gray-100 text-gray-700'
                        }`}
                    >
                        <i className="fas fa-chevron-right"></i>
                    </button>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading candidates...</span>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gradpath-dark">Search Candidates</h3>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                        {activeTab === 'unlocked' 
                            ? `${filteredCandidates.filter(c => isUnlocked(c.id)).length} unlocked candidates`
                            : `${filteredCandidates.length} candidates found`
                        }
                    </span>
                    <button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="text-sm text-gradpath-green hover:text-gradpath-dark transition flex items-center gap-1"
                    >
                        <i className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''}`}></i>
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200 mb-4">
                <button
                    onClick={() => {
                        setActiveTab('all');
                        setCurrentPage(1);
                        setTotalPages(Math.ceil(filteredCandidates.length / itemsPerPage));
                    }}
                    className={`px-4 py-2 text-sm font-medium transition-all ${
                        activeTab === 'all'
                            ? 'text-gradpath-green border-b-2 border-gradpath-green'
                            : 'text-gray-500 hover:text-gradpath-green'
                    }`}
                >
                    All Candidates ({filteredCandidates.length})
                </button>
                <button
                    onClick={() => {
                        setActiveTab('unlocked');
                        setCurrentPage(1);
                        const unlockedCount = filteredCandidates.filter(c => isUnlocked(c.id)).length;
                        setTotalPages(Math.ceil(unlockedCount / itemsPerPage));
                    }}
                    className={`px-4 py-2 text-sm font-medium transition-all ${
                        activeTab === 'unlocked'
                            ? 'text-gradpath-green border-b-2 border-gradpath-green'
                            : 'text-gray-500 hover:text-gradpath-green'
                    }`}
                >
                    Unlocked ({unlockedCandidates.length})
                </button>
            </div>

            <div className="mb-4">
                <div className="relative">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                        type="text"
                        placeholder="Search by name, skills, city, or keywords..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-gradpath-green focus:ring-1 focus:ring-gradpath-green outline-none"
                    />
                </div>
                {isRefreshing && (
                    <p className="text-xs text-gray-400 mt-1 text-right">
                        <i className="fas fa-spinner fa-spin mr-1"></i> Refreshing in background...
                    </p>
                )}
            </div>

            {displayCandidates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <i className="fas fa-user-slash text-4xl text-gray-300 mb-3"></i>
                    <p>{activeTab === 'unlocked' ? 'No unlocked candidates yet.' : 'No candidates found matching your search'}</p>
                </div>
            ) : (
                <>
                    <div className="grid gap-4">
                        {displayCandidates.map(candidate => {
                            const unlocked = isUnlocked(candidate.id);
                            return (
                                <div key={candidate.id} className={`border rounded-lg p-4 hover:shadow-md transition ${unlocked ? 'border-gradpath-green bg-green-50/30' : 'border-gray-200'}`}>
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h4 className="font-bold text-gradpath-dark">
                                                    {candidate.firstName} {candidate.lastName}
                                                </h4>
                                                {unlocked && (
                                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                        <i className="fas fa-check-circle mr-1"></i> Contact Unlocked
                                                    </span>
                                                )}
                                                <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                                                    {candidate._completion || 100}% Complete
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600">{candidate.city}, {candidate.province}</p>
                                            
                                            {candidate.professionalSummary && (
                                                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                                    {candidate.professionalSummary}
                                                </p>
                                            )}
                                            
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {candidate.skills?.slice(0, 5).map((skill, i) => (
                                                    <span key={i} className="bg-green-50 text-gradpath-green px-2 py-0.5 rounded text-xs">
                                                        {skill}
                                                    </span>
                                                ))}
                                                {candidate.skills?.length > 5 && (
                                                    <span className="text-xs text-gray-400">+{candidate.skills.length - 5} more</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col items-end gap-2 ml-4">
                                            <button
                                                onClick={() => handleViewProfile(candidate)}
                                                className="px-4 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                                            >
                                                <i className="fas fa-eye mr-1"></i> View Profile
                                            </button>
                                            {unlocked && (
                                                <span className="text-xs text-green-600">
                                                    <i className="fas fa-check-circle mr-1"></i> Contact info available
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    {renderPagination()}
                </>
            )}

            {/* Profile View Modal with Hidden Contact Info */}
            {selectedCandidate && showProfileModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowProfileModal(false)}>
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setShowProfileModal(false)}
                            className="sticky top-0 float-right m-4 z-10 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md text-gray-400 hover:text-gray-600 text-2xl"
                        >
                            <i className="fas fa-times"></i>
                        </button>

                        <div className="p-6 pt-0">
                            {/* Header - Email hidden if not unlocked */}
                            <div className="pt-6 pb-4 border-b border-gray-200">
                                <h2 className="text-2xl font-bold text-gradpath-dark">
                                    {selectedCandidate.firstName} {selectedCandidate.lastName}
                                </h2>
                                <p>
                                    {contactUnlocked ? selectedCandidate.email : '••••••••@••••.•••'}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                                        {selectedCandidate.userType === 'student' ? 'Student' : 'Graduate'}
                                    </span>
                                    <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
                                        {selectedCandidate._completion || 100}% Complete
                                    </span>
                                    {contactUnlocked && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                            <i className="fas fa-check-circle mr-1"></i> Contact Unlocked
                                        </span>
                                    )}
                                    {!contactUnlocked && (
                                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                            <i className="fas fa-lock mr-1"></i> Contact Locked
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Contact Information - Hidden if not unlocked */}
                            <div className="mt-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-bold text-gradpath-dark">Contact Information</h3>
                                    {!contactUnlocked && (
                                        <span className="text-xs text-gray-400">
                                            <i className="fas fa-lock mr-1"></i> Locked - Unlock to view
                                        </span>
                                    )}
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-500">Email</label>
                                        <p className="font-medium">
                                            {contactUnlocked ? selectedCandidate.email : '••••••••@••••.•••'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Phone</label>
                                        <p className="font-medium">
                                            {contactUnlocked ? (selectedCandidate.phoneNumber || 'Not provided') : '•••••••••••'}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Location</label>
                                        <p className="font-medium">{selectedCandidate.city}, {selectedCandidate.province}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Citizenship</label>
                                        <p className="font-medium">{selectedCandidate.citizenship || 'Not specified'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Professional Links - Hidden if not unlocked */}
                            {(selectedCandidate.linkedin || selectedCandidate.portfolio) && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Professional Links</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        {selectedCandidate.linkedin && (
                                            <div>
                                                <label className="text-sm text-gray-500">
                                                    <i className="fab fa-linkedin text-blue-600 mr-1"></i> LinkedIn
                                                </label>
                                                <p>
                                                    {contactUnlocked ? (
                                                        <a 
                                                            href={selectedCandidate.linkedin} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className="text-gradpath-green hover:underline break-all"
                                                        >
                                                            {selectedCandidate.linkedin}
                                                        </a>
                                                    ) : (
                                                        <span className="text-gray-400">•••••••••••••••</span>
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                        {selectedCandidate.portfolio && (
                                            <div>
                                                <label className="text-sm text-gray-500">
                                                    <i className="fas fa-folder-open text-gradpath-green mr-1"></i> Portfolio
                                                </label>
                                                <p>
                                                    {contactUnlocked ? (
                                                        <a 
                                                            href={selectedCandidate.portfolio} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className="text-gradpath-green hover:underline break-all"
                                                        >
                                                            {selectedCandidate.portfolio}
                                                        </a>
                                                    ) : (
                                                        <span className="text-gray-400">•••••••••••••••</span>
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Job Preferences - Always visible */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="text-lg font-bold text-gradpath-dark mb-4">Job Preferences</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm text-gray-500">Looking For</label>
                                        <p className="font-medium">{selectedCandidate.lookingFor || 'Not specified'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Preferred Location</label>
                                        <p className="font-medium">{selectedCandidate.preferredJobLocation || 'Not specified'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Desired Salary</label>
                                        <p className="font-medium">{selectedCandidate.desiredSalary || 'Not specified'}</p>
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-500">Willing to Relocate</label>
                                        <p className="font-medium">{selectedCandidate.willingToRelocate ? '✅ Yes' : '❌ No'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Skills & Experience - Always visible */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="text-lg font-bold text-gradpath-dark mb-4">Skills & Experience</h3>
                                
                                <div className="mb-4">
                                    <label className="text-sm text-gray-500">Skills</label>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {selectedCandidate.skills?.map((skill, i) => (
                                            <span key={i} className="bg-green-50 text-gradpath-green px-2 py-0.5 rounded-full text-xs">
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="text-sm text-gray-500">Languages</label>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {selectedCandidate.languages?.map((lang, i) => (
                                            <span key={i} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full text-xs">
                                                {lang}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-sm text-gray-500">Experience Level</label>
                                    <p className="font-medium">{selectedCandidate.experienceLevel || 'Not specified'}</p>
                                </div>
                            </div>

                            {/* Professional Summary - Always visible */}
                            {selectedCandidate.professionalSummary && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Professional Summary</h3>
                                    <p className="text-gray-600">{selectedCandidate.professionalSummary}</p>
                                </div>
                            )}

                            {/* Education - Always visible */}
                            {selectedCandidate.education?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Education</h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.education.map((edu, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{edu.qualification}</p>
                                                <p className="text-sm text-gray-600">{edu.institution}</p>
                                                {(edu.startDate || edu.endDate) && (
                                                    <p className="text-sm text-gray-500">
                                                        {edu.startDate || 'Start date not specified'} 
                                                        {edu.isCurrent ? ' - Present' : edu.endDate ? ` - ${edu.endDate}` : ''}
                                                        {edu.isCurrent && (
                                                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                                Current
                                                            </span>
                                                        )}
                                                    </p>
                                                )}
                                                {edu.keyHighlights && (
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        <span className="font-medium">Highlights:</span> {edu.keyHighlights}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Work Experience - Always visible */}
                            {selectedCandidate.workExperience?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">Work Experience</h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.workExperience.map((exp, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{exp.title} at {exp.company}</p>
                                                <p className="text-sm text-gray-500">{exp.startDate} - {exp.endDate || 'Present'}</p>
                                                {exp.description && <p className="text-sm text-gray-600 mt-1">{exp.description}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Certifications & Awards - Always visible */}
                            {selectedCandidate.certifications?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">
                                        <i className="fas fa-certificate text-gradpath-green mr-2"></i>
                                        Certifications & Awards
                                    </h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.certifications.map((cert, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{cert.name}</p>
                                                {cert.issuingOrganization && (
                                                    <p className="text-sm text-gray-600">{cert.issuingOrganization}</p>
                                                )}
                                                {cert.year && (
                                                    <p className="text-sm text-gray-500">{cert.year}</p>
                                                )}
                                                {cert.description && (
                                                    <p className="text-sm text-gray-600 mt-1">{cert.description}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* References - Hidden if not unlocked */}
                            {selectedCandidate.references?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-bold text-gradpath-dark mb-4">
                                        <i className="fas fa-address-book text-gradpath-green mr-2"></i>
                                        References
                                    </h3>
                                    <div className="space-y-3">
                                        {selectedCandidate.references.map((ref, i) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3">
                                                <p className="font-bold">{ref.name}</p>
                                                {ref.title && <p className="text-sm text-gray-600">{ref.title}</p>}
                                                {ref.company && <p className="text-sm text-gray-600">{ref.company}</p>}
                                                {(ref.email || ref.phone) && (
                                                    <div className="mt-1 text-sm text-gray-500">
                                                        {contactUnlocked ? (
                                                            <>
                                                                {ref.email && <span>📧 {ref.email}</span>}
                                                                {ref.phone && <span className="ml-3">📱 {ref.phone}</span>}
                                                            </>
                                                        ) : (
                                                            <span className="text-gray-400">•••••••••••••••</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Unlock Button - Only show if not already unlocked */}
                            {!contactUnlocked && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4">
                                        <div className="flex items-start gap-3">
                                            <i className="fas fa-lock text-yellow-600 text-xl mt-0.5"></i>
                                            <div>
                                                <p className="text-sm text-yellow-800 font-medium">
                                                    Contact Information is Locked
                                                </p>
                                                <p className="text-sm text-yellow-700">
                                                    Unlock this candidate's contact information (email, phone, LinkedIn, portfolio, and reference details) for <strong>1 token</strong>.
                                                </p>
                                                <p className="text-xs text-yellow-600 mt-1">
                                                    Tokens available: <strong>{tokens}</strong>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleUnlockContact}
                                        disabled={unlocking || tokens < 1}
                                        className={`w-full py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                                            unlocking || tokens < 1
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-gradpath-green text-white hover:bg-gradpath-dark'
                                        }`}
                                    >
                                        {unlocking ? (
                                            <i className="fas fa-spinner fa-spin"></i>
                                        ) : (
                                            <i className="fas fa-unlock"></i>
                                        )}
                                        {unlocking ? 'Unlocking...' : tokens < 1 ? 'Insufficient Tokens' : 'Unlock Contact Info (1 token)'}
                                    </button>
                                    {tokens < 1 && (
                                        <p className="text-xs text-red-500 text-center mt-2">
                                            Insufficient tokens. Please contact admin to add more tokens.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Already Unlocked Badge & Download CV Button */}
                            {contactUnlocked && (
                                <>
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <div className="bg-green-50 p-4 rounded-lg border border-green-200 flex items-center gap-3">
                                            <i className="fas fa-check-circle text-green-600 text-xl"></i>
                                            <div>
                                                <p className="text-sm text-green-800 font-medium">Contact Information Unlocked</p>
                                                <p className="text-sm text-green-700">
                                                    You can now view all contact details for this candidate.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Download CV Button */}
                                    <div className="mt-4">
                                        <button
                                            onClick={handleDownloadCV}
                                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
                                        >
                                            <i className="fas fa-file-pdf"></i> Download CV (PDF)
                                        </button>
                                        <p className="text-xs text-gray-400 text-center mt-1">
                                            A professionally formatted PDF will be downloaded
                                        </p>
                                    </div>
                                </>
                            )}

                            {/* Close Button */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setShowProfileModal(false)}
                                    className="w-full px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        {/* Hidden CV Template for PDF generation */}
                        <div className="hidden">
                            <CVPDFTemplate 
                                candidate={selectedCandidate} 
                                contactUnlocked={contactUnlocked} 
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CandidateSearch;